import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { ShoppingBag, Plus, Trash2, PackageCheck, Ban, Eye, Search, Wallet } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';
import { UiSelect } from '../../components/UiSelect';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { SkeletonTable, EmptyState, ButtonLoader } from '../../components/LoadingStates';
import { purchaseService } from '../../services/inventory/purchase';
import { supplierService } from '../../services/inventory/supplier';
import { inventoryItemService } from '../../services/inventory/inventoryItem';
import {
  Purchase, PurchaseLine, PurchaseStatus, PaymentStatus, CreatePurchaseRequest, unitShort,
} from '../../types/inventory';
import { formatCurrency, formatQty, formatDate, formatDateTime } from './inventoryUtils';
import { Modal, Field, Th, inputCls } from './StockItemsTab';

interface Props { org: string; restaurantId: string; }

const STATUS_BADGE: Record<PurchaseStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ORDERED: 'bg-blue-100 text-blue-800',
  RECEIVED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-700',
};

const PAYMENT_BADGE: Record<PaymentStatus, string> = {
  UNPAID: 'bg-red-100 text-red-700',
  PARTIAL: 'bg-amber-100 text-amber-800',
  PAID: 'bg-green-100 text-green-800',
};

const outstandingOf = (p: Purchase) => Math.max(0, (p.totalAmount || 0) - (p.amountPaid || 0));

export const PurchasesTab: React.FC<Props> = ({ org, restaurantId }) => {
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<PurchaseStatus | ''>('');
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [viewing, setViewing] = useState<Purchase | null>(null);
  const [payingFor, setPayingFor] = useState<Purchase | null>(null);
  const [confirm, setConfirm] = useState<{ open: boolean; title: string; message: string; type: 'warning' | 'danger' | 'info'; confirmText: string; onConfirm: () => void }>({
    open: false, title: '', message: '', type: 'warning', confirmText: 'Confirm', onConfirm: () => {},
  });

  const { data: purchases, isLoading } = useQuery(
    ['inventory-purchases', org, restaurantId, statusFilter],
    () => purchaseService.getPurchases({ org, restaurantId, ...(statusFilter && { status: statusFilter }) }),
  );

  const { data: payables } = useQuery(
    ['inventory-payables', org, restaurantId],
    () => purchaseService.getPayables(org, restaurantId),
  );
  const totalOutstanding = (payables ?? []).reduce((s, r) => s + r.outstanding, 0);

  const receiveMutation = useMutation(purchaseService.receivePurchase, {
    onSuccess: () => {
      queryClient.invalidateQueries(['inventory-purchases']);
      queryClient.invalidateQueries(['inventory-items']);
      toast.success('Goods received — stock updated');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to receive purchase'); },
  });
  const cancelMutation = useMutation(purchaseService.cancelPurchase, {
    onSuccess: () => { queryClient.invalidateQueries(['inventory-purchases']); toast.success('Purchase cancelled'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to cancel purchase'); },
  });
  const deleteMutation = useMutation(purchaseService.deletePurchase, {
    onSuccess: () => { queryClient.invalidateQueries(['inventory-purchases']); toast.success('Purchase deleted'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to delete purchase'); },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return purchases ?? [];
    return (purchases ?? []).filter(p =>
      p.purchaseNo?.toLowerCase().includes(q) ||
      p.supplierName?.toLowerCase().includes(q) ||
      p.invoiceNo?.toLowerCase().includes(q),
    );
  }, [purchases, search]);

  const askReceive = (p: Purchase) => setConfirm({
    open: true, title: 'Receive Goods', type: 'info', confirmText: 'Receive',
    message: `Mark purchase ${p.purchaseNo || ''} as received? This will add the ordered quantities to your stock.`,
    onConfirm: () => { receiveMutation.mutate(p.id); setConfirm(c => ({ ...c, open: false })); },
  });
  const askCancel = (p: Purchase) => setConfirm({
    open: true, title: 'Cancel Purchase', type: 'warning', confirmText: 'Cancel Purchase',
    message: `Cancel purchase ${p.purchaseNo || ''}? Stock will not be affected.`,
    onConfirm: () => { cancelMutation.mutate(p.id); setConfirm(c => ({ ...c, open: false })); },
  });
  const askDelete = (p: Purchase) => setConfirm({
    open: true, title: 'Delete Purchase', type: 'danger', confirmText: 'Delete',
    message: `Delete purchase ${p.purchaseNo || ''}? This cannot be undone.`,
    onConfirm: () => { deleteMutation.mutate(p.id); setConfirm(c => ({ ...c, open: false })); },
  });

  return (
    <div>
      {totalOutstanding > 0 && (
        <div className="mb-4 flex items-center gap-3 p-3 rounded-xl border" style={{ backgroundColor: '#fffbeb', borderColor: '#fde68a' }}>
          <Wallet className="h-5 w-5 flex-shrink-0" style={{ color: '#b45309' }} />
          <div className="flex-1 text-sm" style={{ color: '#92400e' }}>
            <span className="font-semibold">{formatCurrency(totalOutstanding)}</span> outstanding to {(payables ?? []).length} supplier{(payables ?? []).length === 1 ? '' : 's'}
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by PO no, supplier or invoice..."
            className="w-full pl-10 pr-4 py-2 rounded-xl border border-[var(--color-border-primary)] bg-[var(--color-background-secondary)] focus:outline-none focus:border-[var(--color-primary-400)] focus:ring-4 focus:ring-[var(--color-primary-100)]" />
        </div>
        <UiSelect
          value={statusFilter}
          onChange={v => setStatusFilter(v as PurchaseStatus | '')}
          options={[
            { value: '', label: 'All statuses' },
            { value: 'DRAFT', label: 'Draft' },
            { value: 'ORDERED', label: 'Ordered' },
            { value: 'RECEIVED', label: 'Received' },
            { value: 'CANCELLED', label: 'Cancelled' },
          ]}
          className="w-full sm:w-52 shrink-0"
        />
        <button onClick={() => setIsFormOpen(true)} className="inline-flex items-center justify-center btn-primary">
          <Plus className="h-5 w-5 mr-2" /> New Purchase
        </button>
      </div>

      {isLoading ? (
        <SkeletonTable rows={6} columns={6} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<ShoppingBag className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No purchases"
          description="Record a purchase to receive stock from a supplier."
          action={<button onClick={() => setIsFormOpen(true)} className="inline-flex items-center btn-primary"><Plus className="h-5 w-5 mr-2" /> New Purchase</button>}
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
          <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
            <thead className="ui-thead">
              <tr><Th>PO No</Th><Th>Supplier</Th><Th>Date</Th><Th className="text-right">Total</Th><Th className="text-right">Outstanding</Th><Th>Status</Th><Th>Payment</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="ui-tbody">
              {filtered.map(p => (
                <tr key={p.id}>
                  <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>{p.purchaseNo || '—'}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{p.supplierName || '—'}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{formatDate(p.receivedAt || p.createdAt)}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium" style={{ color: currentTheme.colors.text.primary }}>{formatCurrency(p.totalAmount)}</td>
                  <td className="px-4 py-3 text-sm text-right" style={{ color: outstandingOf(p) > 0 ? '#b45309' : currentTheme.colors.text.secondary }}>
                    {p.status === 'CANCELLED' ? '—' : formatCurrency(outstandingOf(p))}
                  </td>
                  <td className="px-4 py-3"><span className={clsx('inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium', STATUS_BADGE[p.status])}>{p.status}</span></td>
                  <td className="px-4 py-3">
                    {p.status !== 'CANCELLED' && (
                      <span className={clsx('inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium', PAYMENT_BADGE[p.paymentStatus || 'UNPAID'])}>{p.paymentStatus || 'UNPAID'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setViewing(p)} title="View" className="p-2 rounded-lg" style={{ color: currentTheme.colors.text.secondary, backgroundColor: currentTheme.colors.background.hover }}><Eye className="h-4 w-4" /></button>
                      {p.status !== 'CANCELLED' && outstandingOf(p) > 0 && (
                        <button onClick={() => setPayingFor(p)} title="Record payment" className="p-2 rounded-lg" style={{ color: currentTheme.colors.primary[600], backgroundColor: currentTheme.colors.primary[50] }}><Wallet className="h-4 w-4" /></button>
                      )}
                      {(p.status === 'DRAFT' || p.status === 'ORDERED') && (
                        <>
                          <button onClick={() => askReceive(p)} title="Receive goods" className="p-2 rounded-lg" style={{ color: '#16a34a', backgroundColor: '#f0fdf4' }}><PackageCheck className="h-4 w-4" /></button>
                          <button onClick={() => askCancel(p)} title="Cancel" className="p-2 rounded-lg" style={{ color: '#b45309', backgroundColor: '#fef3c7' }}><Ban className="h-4 w-4" /></button>
                        </>
                      )}
                      {p.status !== 'RECEIVED' && (
                        <button onClick={() => askDelete(p)} title="Delete" className="p-2 rounded-lg" style={{ color: '#dc2626', backgroundColor: '#fef2f2' }}><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isFormOpen && (
        <PurchaseForm org={org} restaurantId={restaurantId} onClose={() => setIsFormOpen(false)}
          onSaved={() => { setIsFormOpen(false); queryClient.invalidateQueries(['inventory-purchases']); }} />
      )}

      {viewing && <PurchaseDetails purchase={viewing} onClose={() => setViewing(null)} />}

      {payingFor && (
        <RecordPaymentModal
          purchase={payingFor}
          onClose={() => setPayingFor(null)}
          onSaved={() => {
            setPayingFor(null);
            queryClient.invalidateQueries(['inventory-purchases']);
            queryClient.invalidateQueries(['inventory-payables']);
          }}
        />
      )}

      <ConfirmationModal
        isOpen={confirm.open}
        onClose={() => setConfirm(c => ({ ...c, open: false }))}
        onConfirm={confirm.onConfirm}
        title={confirm.title} message={confirm.message} confirmText={confirm.confirmText} type={confirm.type}
        isLoading={receiveMutation.isLoading || cancelMutation.isLoading || deleteMutation.isLoading}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Purchase create form (with line-item builder)
// ---------------------------------------------------------------------------
interface DraftLine { inventoryItemId: string; quantity: string; unitCost: string; }

const PurchaseForm: React.FC<{ org: string; restaurantId: string; onClose: () => void; onSaved: () => void }> = ({ org, restaurantId, onClose, onSaved }) => {
  const { currentTheme } = useTheme();
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([{ inventoryItemId: '', quantity: '', unitCost: '' }]);
  const [receiveNow, setReceiveNow] = useState(false);

  const { data: suppliers } = useQuery(['inventory-suppliers', org, restaurantId], () => supplierService.getSuppliers(org, restaurantId));
  const { data: items } = useQuery(['inventory-items', org, restaurantId], () => inventoryItemService.getItems({ org, restaurantId }));

  const createMutation = useMutation(
    (payload: CreatePurchaseRequest) => purchaseService.createPurchase(payload),
    {
      onSuccess: async (created) => {
        if (receiveNow) {
          try { await purchaseService.receivePurchase(created.id); } catch { /* surfaced by interceptor */ }
        }
        toast.success(receiveNow ? 'Purchase received — stock updated' : 'Purchase saved');
        onSaved();
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to save purchase'); },
    },
  );

  const setLine = (i: number, patch: Partial<DraftLine>) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = () => setLines(ls => [...ls, { inventoryItemId: '', quantity: '', unitCost: '' }]);
  const removeLine = (i: number) => setLines(ls => ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls);

  // Auto-fill unit cost from the item's last known cost
  const onPickItem = (i: number, itemId: string) => {
    const item = items?.find(it => it.id === itemId);
    setLine(i, { inventoryItemId: itemId, unitCost: item && !lines[i].unitCost ? String(item.costPerUnit ?? '') : lines[i].unitCost });
  };

  const subTotal = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unitCost) || 0), 0);
  const total = subTotal + (Number(taxAmount) || 0);
  const validLines = lines.filter(l => l.inventoryItemId && Number(l.quantity) > 0);
  const canSave = !!supplierId && validLines.length > 0;

  const handleSave = () => {
    if (!canSave) { toast.error('Pick a supplier and add at least one item with quantity.'); return; }
    const payloadLines: PurchaseLine[] = validLines.map(l => ({
      inventoryItemId: l.inventoryItemId,
      quantity: Number(l.quantity),
      unitCost: Number(l.unitCost) || 0,
    }));
    createMutation.mutate({
      supplierId,
      status: 'ORDERED',
      items: payloadLines,
      taxAmount: Number(taxAmount) || undefined,
      invoiceNo: invoiceNo.trim() || undefined,
      expectedDate: expectedDate || undefined,
      notes: notes.trim() || undefined,
      organizationCode: org,
      restaurantId,
    });
  };

  return (
    <Modal title="New Purchase" onClose={onClose} wide>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Supplier" required>
            <UiSelect value={supplierId} onChange={setSupplierId} placeholder="Select supplier"
              options={[{ value: '', label: 'Select supplier' }, ...(suppliers || []).filter(s => s.active).map(s => ({ value: s.id, label: s.name }))]} />
          </Field>
          <Field label="Invoice No"><input value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} className={inputCls} placeholder="optional" /></Field>
          <Field label="Expected / Invoice Date"><input type="date" value={expectedDate} onChange={e => setExpectedDate(e.target.value)} className={inputCls} /></Field>
        </div>

        {/* Line items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>Items</label>
            <button type="button" onClick={addLine} className="text-sm font-medium flex items-center gap-1" style={{ color: currentTheme.colors.primary[600] }}>
              <Plus className="h-4 w-4" /> Add row
            </button>
          </div>
          <div className="space-y-2">
            {lines.map((l, i) => {
              const item = items?.find(it => it.id === l.inventoryItemId);
              const lineTotal = (Number(l.quantity) || 0) * (Number(l.unitCost) || 0);
              return (
                <div key={i} className="flex gap-2 items-center">
                  <UiSelect value={l.inventoryItemId} onChange={v => onPickItem(i, v)} placeholder="Select item" className="flex-1"
                    options={[{ value: '', label: 'Select item' }, ...(items || []).filter(it => it.active).map(it => ({ value: it.id, label: it.name }))]} />
                  <div className="relative w-28">
                    <input type="number" step="any" min="0" value={l.quantity} onChange={e => setLine(i, { quantity: e.target.value })} className={inputCls} placeholder="Qty" />
                    {item && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">{unitShort(item.unit)}</span>}
                  </div>
                  <input type="number" step="any" min="0" value={l.unitCost} onChange={e => setLine(i, { unitCost: e.target.value })} className={clsx(inputCls, 'w-28')} placeholder="Rate ₹" />
                  <span className="w-24 text-right text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>{formatCurrency(lineTotal)}</span>
                  <button type="button" onClick={() => removeLine(i)} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Tax / Other charges (₹)"><input type="number" step="any" min="0" value={taxAmount} onChange={e => setTaxAmount(e.target.value)} className={inputCls} placeholder="0.00" /></Field>
          <Field label="Notes"><input value={notes} onChange={e => setNotes(e.target.value)} className={inputCls} placeholder="optional" /></Field>
        </div>

        {/* Totals */}
        <div className="rounded-lg p-4 space-y-1" style={{ backgroundColor: currentTheme.colors.background.hover }}>
          <Row label="Subtotal" value={formatCurrency(subTotal)} />
          {Number(taxAmount) > 0 && <Row label="Tax / Charges" value={formatCurrency(Number(taxAmount))} />}
          <div className="flex justify-between pt-1 mt-1 border-t font-semibold" style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.primary }}>
            <span>Total</span><span>{formatCurrency(total)}</span>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: currentTheme.colors.text.primary }}>
          <input type="checkbox" checked={receiveNow} onChange={e => setReceiveNow(e.target.checked)} className="h-4 w-4 rounded" />
          Receive immediately (add to stock now)
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
          <button type="button" onClick={handleSave} disabled={!canSave || createMutation.isLoading}
            className="px-4 py-2 btn-primary">
            {createMutation.isLoading ? <ButtonLoader text="Saving..." /> : (receiveNow ? 'Save & Receive' : 'Save Purchase')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const PurchaseDetails: React.FC<{ purchase: Purchase; onClose: () => void }> = ({ purchase, onClose }) => {
  const { currentTheme } = useTheme();
  return (
    <Modal title={`Purchase ${purchase.purchaseNo || ''}`} onClose={onClose} wide>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Detail label="Supplier" value={purchase.supplierName || '—'} />
          <Detail label="Status" value={purchase.status} />
          <Detail label="Invoice No" value={purchase.invoiceNo || '—'} />
          <Detail label="Date" value={formatDate(purchase.receivedAt || purchase.createdAt)} />
        </div>
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: currentTheme.colors.border.primary }}>
          <table className="min-w-full text-sm">
            <thead className="ui-thead">
              <tr><Th>Item</Th><Th className="text-right">Qty</Th><Th className="text-right">Rate</Th><Th className="text-right">Amount</Th></tr>
            </thead>
            <tbody className="ui-tbody">
              {purchase.items.map((l, i) => (
                <tr key={i}>
                  <td className="px-4 py-2" style={{ color: currentTheme.colors.text.primary }}>{l.itemName || l.inventoryItemId}</td>
                  <td className="px-4 py-2 text-right" style={{ color: currentTheme.colors.text.secondary }}>{formatQty(l.quantity)} {unitShort(l.unit)}</td>
                  <td className="px-4 py-2 text-right" style={{ color: currentTheme.colors.text.secondary }}>{formatCurrency(l.unitCost)}</td>
                  <td className="px-4 py-2 text-right font-medium" style={{ color: currentTheme.colors.text.primary }}>{formatCurrency(l.lineTotal ?? l.quantity * l.unitCost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end">
          <div className="w-full sm:w-64 space-y-1">
            <Row label="Subtotal" value={formatCurrency(purchase.subTotal)} />
            {purchase.taxAmount ? <Row label="Tax / Charges" value={formatCurrency(purchase.taxAmount)} /> : null}
            <div className="flex justify-between pt-1 border-t font-semibold" style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.primary }}>
              <span>Total</span><span>{formatCurrency(purchase.totalAmount)}</span>
            </div>
            {purchase.status !== 'CANCELLED' && (
              <>
                <Row label="Paid" value={formatCurrency(purchase.amountPaid || 0)} />
                <div className="flex justify-between font-semibold" style={{ color: outstandingOf(purchase) > 0 ? '#b45309' : '#16a34a' }}>
                  <span>Outstanding</span><span>{formatCurrency(outstandingOf(purchase))}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {purchase.payments && purchase.payments.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: currentTheme.colors.text.secondary }}>Payment history</p>
            <div className="space-y-1">
              {purchase.payments.map((pay, i) => (
                <div key={i} className="flex justify-between text-sm py-1 border-b" style={{ borderColor: currentTheme.colors.border.primary }}>
                  <span style={{ color: currentTheme.colors.text.secondary }}>{formatDateTime(pay.paidAt)}{pay.mode ? ` · ${pay.mode}` : ''}{pay.note ? ` · ${pay.note}` : ''}</span>
                  <span className="font-medium" style={{ color: currentTheme.colors.text.primary }}>{formatCurrency(pay.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {purchase.notes && <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Note: {purchase.notes}</p>}
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Record supplier payment modal
// ---------------------------------------------------------------------------
const RecordPaymentModal: React.FC<{ purchase: Purchase; onClose: () => void; onSaved: () => void }> = ({ purchase, onClose, onSaved }) => {
  const { currentTheme } = useTheme();
  const outstanding = outstandingOf(purchase);
  const [amount, setAmount] = useState(String(outstanding));
  const [mode, setMode] = useState('CASH');
  const [note, setNote] = useState('');

  const mutation = useMutation(
    () => purchaseService.recordPayment(purchase.id, { amount: Number(amount), mode, note: note.trim() || undefined }),
    {
      onSuccess: () => { toast.success('Payment recorded'); onSaved(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to record payment'); },
    },
  );

  const amt = Number(amount) || 0;
  const invalid = amt <= 0 || amt > outstanding + 0.01;

  return (
    <Modal title={`Record Payment — ${purchase.purchaseNo || purchase.supplierName || ''}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: currentTheme.colors.background.hover }}>
          <span className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Outstanding</span>
          <span className="font-semibold" style={{ color: currentTheme.colors.text.primary }}>{formatCurrency(outstanding)}</span>
        </div>
        <Field label="Amount (₹)">
          <input type="number" step="any" min="0" value={amount} onChange={e => setAmount(e.target.value)} className={inputCls} autoFocus />
        </Field>
        <Field label="Payment Mode">
          <UiSelect value={mode} onChange={setMode}
            options={['CASH', 'UPI', 'CARD', 'BANK', 'CHEQUE'].map(m => ({ value: m, label: m }))} />
        </Field>
        <Field label="Note"><input value={note} onChange={e => setNote(e.target.value)} className={inputCls} placeholder="optional (e.g. ref no)" /></Field>
        {invalid && amt > outstanding && <p className="text-sm text-red-600">Amount exceeds the outstanding balance.</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
          <button type="button" onClick={() => mutation.mutate()} disabled={invalid || mutation.isLoading}
            className="px-4 py-2 btn-primary">
            {mutation.isLoading ? <ButtonLoader text="Saving..." /> : 'Record Payment'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const { currentTheme } = useTheme();
  return <div className="flex justify-between text-sm"><span style={{ color: currentTheme.colors.text.secondary }}>{label}</span><span style={{ color: currentTheme.colors.text.primary }}>{value}</span></div>;
};

const Detail: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const { currentTheme } = useTheme();
  return <div><p className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>{label}</p><p className="font-medium" style={{ color: currentTheme.colors.text.primary }}>{value}</p></div>;
};
